/* eslint-disable sonarjs/no-duplicate-string */
import { Stack } from '@landler/component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { ChartProps } from '@/components/Charts/types';
import {
  applyBenchmarksToChartData,
  getBenchmarkLabel,
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  sortChartData,
} from '@/components/Charts/utils';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';
import { printDDMMYYYY, printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Chart, getXAxisProps, Line, LineChart, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { ChartLegendBar } from './components/LegendBar';
import { ChartTooltip, ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const ndivColors = getColorForIndicator(INDICATOR.EO_VEGETATION_CONDITION);

const CHART_COLORS = {
  ndvi: {
    bar: {
      fill: ndivColors.fill,
    },
  },
} as const;

const factNames = {
  vegetation_condition_ndvi: 'vegetation_condition_ndvi',
  vegetation_condition_ndvi_benchmark_low: 'vegetation_condition_ndvi_benchmark_low',
  vegetation_condition_ndvi_benchmark_moderate: 'vegetation_condition_ndvi_benchmark_moderate',
  vegetation_condition_ndvi_benchmark_high: 'vegetation_condition_ndvi_benchmark_high',
};

export const NDVIHistoricalChart: FC<
  HTMLAttributes<HTMLDivElement> &
    ChartProps & {
      benchmarks: number[];
    } & {
      customBenchmark?: CustomBenchmarkSuccessResponse | null;
    }
> = ({ data, benchmarks, customBenchmark, zoom, setZoom, ...delegated }) => {
  const { t } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, 0.8, 1.2);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = [new Date(firstTick.label).getFullYear(), new Date(lastTick.label).getFullYear()];
  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printYear,
  };

  const chartData = applyBenchmarksToChartData(
    sortedChartData,
    customBenchmark,
    xTicksDomain,
    {
      low: 'vegetation_condition_ndvi_benchmark_low',
      moderate: 'vegetation_condition_ndvi_benchmark_moderate',
      high: 'vegetation_condition_ndvi_benchmark_high',
    },
    extrapolateReferenceLinesChartData,
  );

  const hasLowData = chartData.some((point) => !!point.vegetation_condition_ndvi_benchmark_low);
  const hasModerateData = chartData.some((point) => !!point.vegetation_condition_ndvi_benchmark_moderate);
  const hasHighData = chartData.some((point) => !!point.vegetation_condition_ndvi_benchmark_high);

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          [factNames.vegetation_condition_ndvi]: {
            label: t('shared.ncaDetail.details.ndvi.labels.vegetationCondition'),
            color: CHART_COLORS.ndvi.bar.fill,
          },
        }}
        data-testid='ndvi-chart'
      >
        <LineChart data={chartData}>
          <Line
            dataKey={`${[factNames.vegetation_condition_ndvi]}.value`}
            stroke={CHART_COLORS.ndvi.bar.fill}
            className='ndvi-line'
            strokeWidth={4}
            dot={false}
          />
          {RenderReferenceLine({
            dataKey: 'vegetation_condition_ndvi_benchmark_low.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasLowData, t('global.benchmark.low'), customBenchmark?.name),
            chartData,
            id: 'vegetation_condition_ndvi_benchmark_low',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'vegetation_condition_ndvi_benchmark_moderate.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasModerateData, t('global.benchmark.moderate'), customBenchmark?.name),
            chartData,
            id: 'vegetation_condition_ndvi_benchmark_moderate',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'vegetation_condition_ndvi_benchmark_high.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasHighData, t('global.benchmark.high'), customBenchmark?.name),
            chartData,
            id: 'vegetation_condition_ndvi_benchmark_high',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='ndvi-x-axis' />
          <Chart.YAxis domain={yAxisDomain} allowDataOverflow={true} className='ndvi-y-axis'>
            <Chart.Label>{t('shared.ncaDetail.details.ndvi.labels.ndvi')}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={(props) => <CustomTooltip {...props} benchmarks={benchmarks} />} />
        </LineChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('shared.ncaDetail.details.ndvi.labels.vegetationCondition'),
      color: CHART_COLORS.ndvi.bar.fill,
      shape: 'line',
    },
  ];

  return <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='ndvi-chart-legend' />;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  benchmarks,
}: TooltipProps<ValueType, NameType> & { benchmarks: number[] }) => {
  const { t } = useTranslation();

  const ndviValue = payload?.find((data) => data.dataKey === `${[factNames.vegetation_condition_ndvi]}.value`);
  const ndviDisplay = !isNullish(ndviValue?.value)
    ? getDisplayNumber(ndviValue?.value as string, window.navigator.language)
    : t('global.analysis.noData');

  const dataPointHasLabel = ndviValue && ndviValue?.payload?.[factNames.vegetation_condition_ndvi]?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? ndviValue.payload[factNames.vegetation_condition_ndvi].labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;

  const ndviBenchmarkType = useBenchmarkForFact(benchmarks, ndviValue?.value as string);

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printDDMMYYYY(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {ndviBenchmarkType && (
        <div className='typography-body2 text-text-secondary'>
          <BenchmarkPill benchmarkType={ndviBenchmarkType} />
        </div>
      )}
      <Stack spacing={8} direction='row'>
        <ChartTooltipTextPrimary>
          {t('shared.ncaDetail.details.ndvi.labels.vegetationCondition')}
        </ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{ndviDisplay}</ChartTooltipTextPrimary>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipNDVIHistorical };
